// components/DailyReport/UnaffiliatedMemberReportDialog.tsx

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  MenuItem,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { unaffiliatedMemberReportService } from '../../services/unaffiliatedMemberReport';
import { GroupedMemberData, OvertimeSummary } from '../../types/unaffiliatedMemberReport';

interface UnaffiliatedMemberReportDialogProps {
  open: boolean;
  onClose: () => void;
  year: number;
  month: number;
  onYearChange: (year: number) => void;
  onMonthChange: (month: number) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const UnaffiliatedMemberReportDialog: React.FC<UnaffiliatedMemberReportDialogProps> = ({
  open,
  onClose,
  year,
  month,
  onYearChange,
  onMonthChange
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [groupedMembers, setGroupedMembers] = useState<GroupedMemberData[]>([]);
  const [overtimeSummary, setOvertimeSummary] = useState<OvertimeSummary[]>([]);
  
  // 年月変更用
  const [localYear, setLocalYear] = useState(year);
  const [localMonth, setLocalMonth] = useState(month);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, year, month]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // グループ化されたデータとサマリーを並行で取得
      const [groupedResponse, summaryResponse] = await Promise.all([
        unaffiliatedMemberReportService.getGroupedByMonth(year, month),
        unaffiliatedMemberReportService.getOvertimeSummary(year, month)
      ]);
      
      setGroupedMembers(groupedResponse.members);
      setOvertimeSummary(summaryResponse.summary);
    } catch (err) {
      console.error('無所属メンバーデータ取得エラー:', err);
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      month: '2-digit',
      day: '2-digit'
    });
  };

  const handleSearch = () => {
    onYearChange(localYear);
    onMonthChange(localMonth);
    loadData();
  };

  const renderMobileGroupedView = () => {
    if (groupedMembers.length === 0) {
      return (
        <Typography align="center" color="textSecondary" sx={{ my: 4 }}>
          該当する無所属メンバーはいません
        </Typography>
      );
    }

    return (
      <Box>
        {groupedMembers.map((member, index) => (
          <Accordion key={index} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography sx={{ flexGrow: 1, fontWeight: 'medium' }}>
                  {member.memberName}
                </Typography>
                <Typography color="primary" sx={{ fontWeight: 'bold' }}>
                  {member.totalOvertimeHours}h
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {member.records.map((record, recordIndex) => (
                <Paper key={recordIndex} sx={{ p: 2, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CalendarMonthIcon sx={{ mr: 1, fontSize: '0.9rem', color: 'text.secondary' }} />
                    <Typography variant="body2">{formatDate(record.date)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <BusinessIcon sx={{ mr: 1, fontSize: '0.9rem', color: 'text.secondary' }} />
                    <Typography variant="body2">{record.customerName}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationOnIcon sx={{ mr: 1, fontSize: '0.9rem', color: 'text.secondary' }} />
                    <Typography variant="body2">{record.siteName}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AccessTimeIcon sx={{ mr: 1, fontSize: '0.9rem', color: 'text.secondary' }} />
                    <Typography variant="body2">残業: {record.overtimeHours}h</Typography>
                  </Box>
                </Paper>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    );
  };

  const renderDesktopGroupedView = () => {
    if (groupedMembers.length === 0) {
      return (
        <Typography align="center" color="textSecondary" sx={{ my: 4 }}>
          該当する無所属メンバーはいません
        </Typography>
      );
    }

    return (
      <Box>
        {groupedMembers.map((member, index) => (
          <Paper key={index} sx={{ mb: 3, overflow: 'hidden' }}>
            <Box sx={{ 
              bgcolor: 'primary.main', 
              color: 'white', 
              px: 2, 
              py: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography variant="subtitle1">
                <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {member.memberName}
              </Typography>
              <Typography variant="subtitle1">
                合計残業時間: {member.totalOvertimeHours}h
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>日付</TableCell>
                    <TableCell>客先名</TableCell>
                    <TableCell>現場名</TableCell>
                    <TableCell align="right">残業時間</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {member.records.map((record, recordIndex) => (
                    <TableRow key={recordIndex}>
                      <TableCell>{formatDate(record.date)}</TableCell>
                      <TableCell>{record.customerName}</TableCell>
                      <TableCell>{record.siteName}</TableCell>
                      <TableCell align="right">{record.overtimeHours}h</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))}
      </Box>
    );
  };

  const renderMobileSummaryView = () => {
    if (overtimeSummary.length === 0) {
      return (
        <Typography align="center" color="textSecondary" sx={{ my: 4 }}>
          該当する残業データはありません
        </Typography>
      );
    }

    return (
      <Box>
        {overtimeSummary.map((item, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography>{item.memberName}</Typography>
            </Box>
            <Typography fontWeight="bold" color="primary">
              {item.totalOvertime}h
            </Typography>
          </Paper>
        ))}
      </Box>
    );
  };

  const renderDesktopSummaryView = () => {
    if (overtimeSummary.length === 0) {
      return (
        <Typography align="center" color="textSecondary" sx={{ my: 4 }}>
          該当する残業データはありません
        </Typography>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>メンバー名</TableCell>
              <TableCell align="right">合計残業時間</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {overtimeSummary.map((item, index) => (
              <TableRow key={index}>
                <TableCell>{item.memberName}</TableCell>
                <TableCell align="right">{item.totalOvertime}h</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallScreen}
    >
      <DialogTitle>
        無所属メンバーレポート
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            flexDirection: isSmallScreen ? 'column' : 'row',
            alignItems: isSmallScreen ? 'stretch' : 'flex-end',
            mb: 2
          }}>
            <TextField
              select
              label="年"
              value={localYear}
              onChange={(e) => setLocalYear(Number(e.target.value))}
              fullWidth={isSmallScreen}
              sx={{ minWidth: '100px' }}
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                <MenuItem key={y} value={y}>{y}年</MenuItem>
              ))}
            </TextField>
            
            <TextField
              select
              label="月"
              value={localMonth}
              onChange={(e) => setLocalMonth(Number(e.target.value))}
              fullWidth={isSmallScreen}
              sx={{ minWidth: '100px' }}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <MenuItem key={m} value={m}>{m}月</MenuItem>
              ))}
            </TextField>
            
            <Button 
              variant="contained" 
              onClick={handleSearch}
              fullWidth={isSmallScreen}
            >
              検索
            </Button>
          </Box>
          
          <Divider sx={{ my: 2 }} />

          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant={isSmallScreen ? "fullWidth" : "standard"}
            centered
          >
            <Tab label="メンバー別詳細" />
            {/* <Tab label="残業時間サマリー" /> */}
          </Tabs>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center" my={4}>
            {error}
          </Typography>
        ) : (
          <>
            <TabPanel value={tabValue} index={0}>
              {isSmallScreen ? renderMobileGroupedView() : renderDesktopGroupedView()}
            </TabPanel>
            
            <TabPanel value={tabValue} index={1}>
              {isSmallScreen ? renderMobileSummaryView() : renderDesktopSummaryView()}
            </TabPanel>
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="primary">
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnaffiliatedMemberReportDialog;